type MathOperations = {
  [key: string]: (a: number, b: number) => number;
};
// Initialize mathFun with JavaScript's built-in function
const mathFun: MathOperations = {
  add: (a: number, b: number) => a + b,
  subtract: (a: number, b: number) => a - b,
  multiply: (a: number, b: number) => a * b,
  divide: (a: number, b: number) => (b !== 0 ? a / b : 0), // Avoid divide by zero
  abs: (a: number) => Math.abs(a),
  max: (a: number, b: number) => Math.max(a, b),
  min: (a: number, b: number) => Math.min(a, b),
  round: (a: number) => Math.round(a),
};
//  Select specific columns from the data.
export function selectColumn(data: any[], columns: string[]): any[] {
  return data.map((row) =>
    columns.reduce((acc, col) => {
      if (row.hasOwnProperty(col)) acc[col] = row[col];
      return acc;
    }, {} as any)
  );
}
//  Group data by a column and apply aggregation functions.
export function groupBy(data: any[], columns: string[], parameter: any[]) {
  const groupedData = data.reduce((acc, obj) => {
    // unique key
    const key = columns.map((col) => obj[col]).join('-');
    if (!acc[key]) {
      // Copy initial values
      acc[key] = { ...obj };
    } else {
      // apply aggregate function
      parameter.forEach((aggregation) => {
        // group on table
        Object.entries(aggregation).forEach(([col, operation]) => {
          if (operation === 'sum') {
            acc[key][col] = (acc[key][col] || 0) + (obj[col] || 0);
          }
        });
      });
    }
    return acc;
  }, {});

  return Object.values(groupedData);
}
/**
 * Calculate the difference between two columns and store it in a new column.
 */
export function difference(
  data: any[],
  newColumn: string,
  columns: [string, string]
): any[] {
  return data.map((row) => ({
    ...row,
    [newColumn]: (row[columns[0]] || 0) - (row[columns[1]] || 0),
  }));
}

export function applyMathFunction(
  data: any[],
  column: string,
  operation: { function: keyof typeof mathFun } // Pass the operation as an object to handle single/multiple args
): any[] {
  return data.map((row) => {
    const mathOperation = mathFun[operation.function];
    // Handle operations that require two arguments (e.g., 'add', 'subtract', etc.)
    if (operation.function !== 'abs') {
      if (typeof mathOperation === 'function') {
        row[column] = mathOperation(row[column] || 0, row[column] || 0);
      } else {
        throw new Error(`Invalid operation: ${operation.function}`);
      }
    } else {
      // Handle 'abs' operation which only needs one argument
      if (typeof mathOperation === 'function') {
        row[column] = mathOperation(row[column] || 0, row[column] || 0);
      } else {
        throw new Error(`Invalid operation: ${operation.function}`);
      }
    }

    return row;
  });
}
/**
 * Rename columns in the data.
 */
export function renameColumn(
  data: any[],
  renames: { old_name: string; new_name: string }[]
): any[] {
  return data.map((row) =>
    renames.reduce(
      (newRow, { old_name, new_name }) => {
        if (row.hasOwnProperty(old_name)) {
          newRow[new_name] = row[old_name];
          delete newRow[old_name];
        }
        return newRow;
      },
      { ...row }
    )
  );
}

export function retrieveArray(
  data: any,
  column: any,
  parameter: { column: string },
  metaData: any
): any {
  const results = data.map((row: any, index: any) => {
    const updatedPath = parameter.column
      .split('.')
      .map((item, i) => (i === 1 ? index.toString() : item));
    const value = updatedPath.reduce((acc, key) => acc?.[key], metaData);
    return {
      ...row,
      [column]: value,
    };
  });
  return results;
}

export function retrieveObject(
  data: any,
  column: any,
  parameter: { column: string },
  metaData: any
): any {
  const results = data.map((row: any) => {
    const value = parameter.column
      .split('.')
      .reduce((acc, key) => acc?.[key], metaData);
    return {
      ...row,
      [column]: value,
    };
  });

  return results;
}
export function extractName(
  data: any[],
  column: string,
  parameter: { prefixesToRemove: string[] }
): any[] {
  return data.map((row) => {
    // Skip if not a string
    if (typeof row[column] !== 'string') return row;

    let value = row[column].trim();
    const lowerValue = value.toLowerCase();

    // Check each prefix
    for (const prefix of parameter.prefixesToRemove) {
      const lowerPrefix = prefix.toLowerCase();

      if (lowerValue.startsWith(lowerPrefix)) {
        // Remove the exact prefix's length from original string
        value = value.slice(prefix.length).trimStart();
        break; // Stop after first match
      }
    }

    return {
      ...row,
      [column]: value,
    };
  });
}

export function mapping(
  data: any[],
  column: string,
  parameter: {
    dictData: { [key: string]: string }; // Now expects a key-value map
  }
): any[] {
  return data.map((row) => ({
    ...row,
    [column]: parameter.dictData[row[column]] || row[column],
  }));
}
